/**
 * FriendContentPage - View a friend's lists and history
 * Only accessible for mutual friends
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  Tv, Film, Mic, BookOpen, List, Eye, ThumbsUp, Meh, ThumbsDown, 
  Loader2, ArrowLeft, Lock, Trophy, Star, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grade configurations
const GRADES = {
  liked: { icon: ThumbsUp, color: 'text-green-600 bg-green-50', label: 'Liked' },
  alright: { icon: Meh, color: 'text-amber-600 bg-amber-50', label: 'Alright' },
  disliked: { icon: ThumbsDown, color: 'text-red-600 bg-red-50', label: 'Disliked' },
};

function ListCard({ list, mediaType, onClick }) {
  const itemCount = list.series?.length || list.books?.length || list.podcasts?.length || 0;
  const coverImage = list.series?.[0]?.poster_path || 
                     list.books?.[0]?.cover_image || 
                     list.podcasts?.[0]?.image;
  
  const MediaIcon = mediaType === 'tv' || mediaType === 'movie' 
    ? (list.media_category === 'movie' ? Film : Tv)
    : mediaType === 'book' ? BookOpen : Mic;
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md hover:border-[#F87171]/30 transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
          {coverImage ? (
            <img src={coverImage} alt={list.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MediaIcon className="w-5 h-5 text-slate-400" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-[#3D4F6F] truncate">{list.title}</h3>
          <p className="text-sm text-[#64748B]">{itemCount} items</p>
          {list.list_type === 'joint' && (
            <Badge variant="outline" className="mt-1 text-xs">Joint List</Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function HistoryItem({ item, mediaType, showScore }) {
  const grade = GRADES[item.grade] || GRADES.liked;
  const GradeIcon = grade.icon;
  
  const title = item.title || item.name;
  const image = item.poster_path || item.cover_image || item.image;
  
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm border border-slate-100">
      <span className="font-outfit font-bold text-lg text-[#3D4F6F] w-8 text-center">
        {item.rank_in_group || '-'}
      </span>
      <div className="w-10 h-14 rounded overflow-hidden bg-slate-100 flex-shrink-0">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {mediaType === 'tv' && <Tv className="w-4 h-4 text-slate-400" />}
            {mediaType === 'movie' && <Film className="w-4 h-4 text-slate-400" />}
            {mediaType === 'book' && <BookOpen className="w-4 h-4 text-slate-400" />}
            {mediaType === 'podcast' && <Mic className="w-4 h-4 text-slate-400" />}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-[#3D4F6F] truncate text-sm">{title}</h4>
        {item.date_watched && (
          <p className="text-xs text-[#64748B]">
            {new Date(item.date_watched).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-full ${grade.color}`}>
          <GradeIcon className="w-3 h-3" />
        </div>
        {showScore && item.calculated_score !== undefined && (
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded-full">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <span className="text-xs font-medium text-amber-700">
              {item.calculated_score.toFixed(1)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FriendContentPage() {
  const { friendId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [friend, setFriend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lists');
  const [historyType, setHistoryType] = useState('tv');
  
  // Data states
  const [lists, setLists] = useState({ watchlists: [], book_lists: [], podcast_lists: [] });
  const [history, setHistory] = useState({ items: [], show_scores: false });
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
        setUser(response.data);
      } catch (error) {
        navigate('/');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch friend profile and lists
  useEffect(() => {
    const fetchFriendData = async () => {
      if (!user || !friendId) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch friend's profile
        const profileRes = await axios.get(`${API}/profile/public/${friendId}`, { withCredentials: true, headers });
        setFriend(profileRes.data);
        
        // Fetch friend's lists
        const listsRes = await axios.get(`${API}/messages/friend/${friendId}/lists`, { withCredentials: true, headers });
        setLists(listsRes.data);
        
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error('You can only view content from mutual friends');
          navigate(-1);
        } else {
          toast.error('Failed to load friend data');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchFriendData();
  }, [user, friendId, navigate]);
  
  // Fetch history when tab changes
  const fetchHistory = async (mediaType) => {
    if (!friendId) return;
    
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${API}/messages/friend/${friendId}/history/${mediaType}`,
        { withCredentials: true, headers }
      );
      
      setHistory({
        items: response.data.items || [],
        show_scores: response.data.show_scores
      });
    } catch (error) {
      console.error('Failed to fetch history:', error);
      setHistory({ items: [], show_scores: false });
    } finally {
      setLoadingHistory(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'history' && friendId) {
      fetchHistory(historyType);
    }
  }, [activeTab, historyType, friendId]);
  
  if (!user || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2FF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
      </div>
    );
  }
  
  const allLists = [
    ...lists.watchlists.map(l => ({ ...l, type: 'watchlist' })),
    ...lists.book_lists.map(l => ({ ...l, type: 'book' })),
    ...lists.podcast_lists.map(l => ({ ...l, type: 'podcast' })),
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2FF]">
      <AppHeader user={user} showBack backPath={`/profile/${friendId}`} backLabel="Profile" />
      
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Friend Header */}
        <Card className="border-0 shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-[#F87171] to-[#FB923C] p-6">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-4 border-white shadow-lg">
                {friend?.avatar_url || friend?.picture ? (
                  <AvatarImage src={friend.avatar_url || friend.picture} />
                ) : (
                  <AvatarFallback className="bg-white text-[#F87171] text-xl">
                    {friend?.display_name?.charAt(0) || friend?.name?.charAt(0) || '?'}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1">
                <h1 className="font-outfit text-2xl font-bold text-white">
                  {friend?.display_name || friend?.name}
                </h1>
                <p className="text-white/80 text-sm">Viewing their lists and history</p>
              </div>
              <Button
                onClick={() => navigate(`/messages/${friendId}`)}
                className="bg-white/20 hover:bg-white/30 text-white rounded-full"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            </div>
          </div>
        </Card>
        
        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto mb-6">
            <TabsTrigger value="lists" className="data-[state=active]:bg-[#F87171] data-[state=active]:text-white">
              <List className="w-4 h-4 mr-2" />
              Lists
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#F87171] data-[state=active]:text-white">
              <Eye className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="lists">
            {allLists.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="text-center py-12">
                  <List className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
                  <p className="text-[#64748B]">No lists yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allLists.map((list) => (
                  <ListCard
                    key={list.id}
                    list={list}
                    mediaType={list.type}
                    onClick={() => {
                      // Navigate to appropriate list detail page
                      if (list.type === 'book') {
                        navigate(`/book-list/${list.id}`);
                      } else if (list.type === 'podcast') {
                        navigate(`/podcast-list/${list.id}`);
                      } else {
                        navigate(`/watchlist/${list.id}`);
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="history">
            {/* History Type Selector */}
            <div className="flex justify-center gap-2 mb-6">
              {[
                { type: 'tv', icon: Tv, label: 'TV' },
                { type: 'movie', icon: Film, label: 'Movies' },
                { type: 'book', icon: BookOpen, label: 'Books' },
                { type: 'podcast', icon: Mic, label: 'Podcasts' },
              ].map(({ type, icon: Icon, label }) => (
                <Button
                  key={type}
                  variant={historyType === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setHistoryType(type)}
                  className={`rounded-full ${historyType === type ? 'bg-[#F87171] hover:bg-[#EF4444]' : ''}`}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {label}
                </Button>
              ))}
            </div>
            
            {/* Score Warning */}
            {!history.show_scores && history.items.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg mb-4 text-sm text-amber-700">
                <Lock className="w-4 h-4" />
                Scores hidden until 10+ items are rated
              </div>
            )}
            
            {loadingHistory ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#F87171]" />
              </div>
            ) : history.items.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="text-center py-12">
                  <Eye className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
                  <p className="text-[#64748B]">No {historyType} history yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Group by grade */}
                {['liked', 'alright', 'disliked'].map((gradeKey) => {
                  const gradeItems = history.items.filter(i => i.grade === gradeKey);
                  if (gradeItems.length === 0) return null;
                  
                  const grade = GRADES[gradeKey];
                  const GradeIcon = grade.icon;
                  
                  return (
                    <div key={gradeKey}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-1.5 rounded-full ${grade.color}`}>
                          <GradeIcon className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-[#3D4F6F]">{grade.label}</span>
                        <span className="text-sm text-[#64748B]">({gradeItems.length})</span>
                      </div>
                      <div className="space-y-2 mb-6">
                        {gradeItems.map((item) => (
                          <HistoryItem
                            key={item.id}
                            item={item}
                            mediaType={historyType}
                            showScore={history.show_scores}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
